import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CollectionsOutlinedIcon from '@mui/icons-material/CollectionsOutlined';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ProductImage } from '../../../types';

interface ProductImageThumbnailsProps {
  alternativeImages: ProductImage[];
  shelfImages: ProductImage[];
  otherChainImages: ProductImage[];
  currentChainId: number;
  chainNamesMap: Record<number, string>;
  onManageClick: () => void;
  onImageClick: (imageUrl: string) => void;
}

export const ProductImageThumbnails: React.FC<ProductImageThumbnailsProps> = ({
  alternativeImages,
  shelfImages,
  otherChainImages,
  currentChainId,
  chainNamesMap,
  onManageClick,
  onImageClick,
}) => {
  const allImages = [...alternativeImages, ...otherChainImages, ...shelfImages];
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    updateScrollState();
  }, [allImages.length, updateScrollState]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollState);
    return () => el.removeEventListener('scroll', updateScrollState);
  }, [updateScrollState]);

  const scroll = useCallback((direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = 97; // thumbnail width (89) + gap (8)
    el.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
  }, []);

  return (
    <div>
      <p className="text-sm text-gray-500 mb-2">Alternative Images</p>
      <div className="relative mb-3">
        {canScrollLeft && (
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-0.5 bg-white border border-gray-300 rounded-full shadow hover:bg-gray-50"
          >
            <ChevronLeftIcon sx={{ fontSize: 18 }} />
          </button>
        )}
        <div
          ref={scrollRef}
          className="flex gap-2 overflow-x-auto scrollbar-hide"
          style={{ scrollbarWidth: 'none' }}
        >
          {allImages.length > 0 ? (
            allImages.map((image, index) => {
              const isShelf = !image.productImageId;
              const isOtherChain = image.chainId !== currentChainId;
              return (
                <div
                  key={image.productImageId ?? `shelf-${index}`}
                  className={`relative w-[89px] h-[89px] flex-shrink-0 border border-gray-300 rounded-md flex items-center justify-center overflow-hidden cursor-pointer ${isShelf ? 'bg-gray-50' : 'bg-white'}`}
                  onClick={() => onImageClick(image.imageUri)}
                >
                  <img
                    src={image.imageUri}
                    alt={`Alternative ${index + 1}`}
                    className={`w-full h-full object-contain ${isShelf ? 'opacity-75' : ''}`}
                  />
                  {isShelf ? (
                    <span className="absolute bottom-0 left-0 right-0 bg-gray-800 bg-opacity-70 text-white text-[9px] text-center py-0.5 truncate px-1">
                      Shelf
                    </span>
                  ) : isOtherChain ? (
                    <span className="absolute bottom-0 left-0 right-0 bg-gray-800 bg-opacity-70 text-white text-[9px] text-center py-0.5 truncate px-1">
                      {chainNamesMap[image.chainId] ||
                        (image.chainId ? `Chain ${image.chainId}` : 'No chain')}
                    </span>
                  ) : null}
                </div>
              );
            })
          ) : (
            <div
              className="w-[89px] h-[89px] border border-gray-300 rounded-md flex items-center justify-center bg-white cursor-pointer hover:bg-gray-50"
              onClick={onManageClick}
            >
              <AddIcon className="text-gray-300" />
            </div>
          )}
        </div>
        {canScrollRight && (
          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-0.5 bg-white border border-gray-300 rounded-full shadow hover:bg-gray-50"
          >
            <ChevronRightIcon sx={{ fontSize: 18 }} />
          </button>
        )}
      </div>
      <button
        onClick={onManageClick}
        className="w-full flex items-center justify-center gap-2 px-4 py-2 border-2 border-green-600 text-green-600 rounded-md hover:bg-green-50 text-sm font-medium"
      >
        <CollectionsOutlinedIcon sx={{ fontSize: 18 }} />
        Manage Product Image
      </button>
    </div>
  );
};
